import { useEffect, useState } from 'react';
import { projectsApiService } from '../../../../graphql/projects/projectsApiService';
import { useDebounceValue } from '../../../../logic/otherUtilities/useDebounceValue';

export interface SiteEvaluationState {
  url: string;
  status?: number;
  timeout?: boolean;
  error?: boolean;
  loading: boolean;
}

export function useSiteEvaluation(formProtocol: string, formDomain: string) {
  const checkIfDomainExistsMutation = projectsApiService.mutations.domains.useCheckIfDomainExists();
  const [siteEvaluationState, setSiteEvaluationState] = useState<SiteEvaluationState>();

  const handleEvaluateDomain = (url: string) => {
    if (!url?.length) return;

    // Set status to loading
    setSiteEvaluationState((prev) => {
      if (prev?.url === url) return prev; // But don't do it if we've already checked this URL
      return { url, loading: true };
    });

    checkIfDomainExistsMutation({ url: url })
      // Update status
      .then((result) =>
        setSiteEvaluationState((prev) => {
          if (prev?.url !== url) return prev;
          return {
            url,
            status: result.statusCode,
            timeout: result.timeout,
            error: result.error,
            loading: false,
          };
        })
      )
      // In case backend crash
      .catch(() => {
        setSiteEvaluationState((prev) => {
          if (prev?.url !== url) return prev;
          return {
            url,
            error: true,
            loading: false,
          };
        });
      });
  };

  const debouncedDomain = useDebounceValue(formDomain, 1000);

  useEffect(() => {
    const fullUrl = formProtocol + debouncedDomain;

    if (
      debouncedDomain === formDomain &&
      fullUrl !== siteEvaluationState?.url &&
      !!debouncedDomain?.length
    ) {
      handleEvaluateDomain(fullUrl);
    }
    if (debouncedDomain !== formDomain) {
      setSiteEvaluationState(undefined);
    }
  }, [formDomain, formProtocol, debouncedDomain]);

  return { siteEvaluationState, handleEvaluateDomain };
}
